import {
  EID_REGEX,
  ICCID_MAX_LEN,
  ICCID_REGEX,
  IMEI_REGEX,
  IMSI_MAX_LEN,
  IMSI_REGEX,
  NON_DIGITS_ICCID_REGEX,
  NON_DIGITS_REGEX,
  ONLY_DIGITS_AND_COMMA_REGEX,
  STRING_NUMBERS_DASH_AND_UNDERSCORES,
  SUBSCRIBER_ID,
} from '@whistler-ui/k2/constants/validations';
import { useAppMessages } from '@whistler-ui/k2/hooks/useAppMessages';
import {
  CompanionSim,
  DataStructure,
  FieldConfig,
  FieldValidation,
  FlattenedObject,
  FormData,
  FormValuesMap,
  Parameter,
  ValidationSchema,
} from '@whistler-ui/portal/components/TestDataPane/types';
import type { TFunction } from 'i18next';
import * as yup from 'yup';

export const regexMap: Record<string, any> = {
  IMSI: {
    regex: IMSI_REGEX,
    props: {
      maxLength: IMSI_MAX_LEN,
      inputMode: 'numeric',
      onChangeRegex: NON_DIGITS_REGEX,
    },
  },
  esim_iccid: {
    regex: ICCID_REGEX,
    props: {
      maxLength: ICCID_MAX_LEN,
      onChangeRegex: NON_DIGITS_ICCID_REGEX,
    },
  },
  device_eid: { regex: EID_REGEX },
  sim_type: {},
  device_imei1: {
    regex: IMEI_REGEX,
    props: {
      onChangeRegex: NON_DIGITS_REGEX,
    },
  },
  device_imei2: {
    regex: IMEI_REGEX,
    props: {
      onChangeRegex: NON_DIGITS_REGEX,
    },
  },
  device_oem: {},
  challenge: {},
  challenge_response: {},
  subscriber_id: { regex: SUBSCRIBER_ID },
  sip_username: { regex: SUBSCRIBER_ID },
  display_name: { regex: STRING_NUMBERS_DASH_AND_UNDERSCORES },
  plan_offer_id: { regex: STRING_NUMBERS_DASH_AND_UNDERSCORES },
  plan_type: {},
  device_type: {},
  subscription_type: {},
  companion_sims: { regex: ONLY_DIGITS_AND_COMMA_REGEX },
};

/**
 * Retrieves the regex pattern for a given field name.
 *
 */
export const getRegex = (name: string): RegExp | undefined => {
  const segments = name.split('--');
  const key = segments[segments.length - 1];
  return regexMap[key]?.regex;
};

/**
 * Generates a flat map of form initial values from nested parameter configurations.
 */
export const generateFormValuesMap = (
  parameters: Parameter[],
  parentKey = '',
  data: FormData,
): FormValuesMap => {
  let formValues: FormValuesMap = {};

  parameters.forEach(param => {
    const currentKey = parentKey ? `${parentKey}--${param.name}` : param.name;

    if (param.contains && param.contains.length > 0) {
      // If the field contains nested parameters, mark as parent and recurse
      if (!data.fields.parents[param.name]) {
        data.fields.parents[param.name] = { required: param.required };
      }

      // Recurse and merge nested form values
      formValues = {
        ...formValues,
        ...generateFormValuesMap(param.contains, currentKey, data),
      };
    } else {
      // Initialize leaf field as empty string
      formValues[currentKey] = '';

      // Create field configuration
      const fieldConfig: FieldConfig = {
        type: param.type,
        required: param.required,
        regex: getRegex(param.name),
        name: currentKey,
        originalName: param.name,
        options: param.options || [],
      };

      // Store field configuration and add to validation rules
      data.fields[currentKey] = fieldConfig;
      data.rules.push(fieldConfig);
    }
  });

  return formValues;
};

/**
 * Flattens a nested object structure into a single-level object with concatenated keys.
 */
export const flattenObject = (
  obj: Record<string, any>,
  parentKey = '',
  result: FlattenedObject = {},
  hasQueryParams = false,
): FlattenedObject => {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Ignore nested key mapping for queryParams
      const fullKey =
        !hasQueryParams && parentKey ? `${parentKey}--${key}` : key;

      if (
        typeof obj[key] === 'object' &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        // If the value is an object, recurse
        flattenObject(obj[key], fullKey, result, hasQueryParams);
      } else {
        // Special handling for companion_sims arrays
        if (fullKey.includes('companion_sims')) {
          result[fullKey] = (obj[key] as CompanionSim[])
            .map(v => v.iccid)
            .join(',');
        } else {
          result[fullKey] = obj[key];
        }
      }
    }
  }

  // Map entitlements string into string[] when hasQueryParams is true
  if (
    hasQueryParams &&
    result?.entitlements &&
    typeof result.entitlements === 'string'
  ) {
    result.entitlements = result.entitlements.split('|');
  }

  return result;
};

/**
 * Generates a Yup validation schema based on field validation configurations.
 */
export const getValidationSchema = (
  validations: FieldValidation[],
): ValidationSchema => {
  return validations.reduce((acc, { name, originalName }) => {
    // Ignore entitlements field as it will be mapped to a string before submission
    if (name === 'entitlements') {
      return acc;
    }

    // Build the validation schema with conditional requirements
    const schema = yup
      .string()
      .when('fields', {
        is: (fields: any) => {
          const segments = name.split('--');
          const isRequired = fields && fields[name]?.required;

          // Check if all parent fields are required, otherwise ignore this field
          if (isRequired && segments.length > 1) {
            for (let i = 0; i < segments.length - 1; i++) {
              if (!fields.parents[segments[i]]?.required) {
                return false;
              }
            }
          }

          return isRequired;
        },
        then: yup
          .string()
          .required(
            `componentTestDataPane_form_field_error_${originalName}_required`,
          ),
      })
      .when('fields', {
        is: (fields: any) => fields && fields[name]?.regex,
        then: yup.string().matches(getRegex(name), {
          message: `componentTestDataPane_form_field_error_${originalName}_invalid`,
        }),
      });

    acc[name] = schema;
    return acc;
  }, {} as ValidationSchema);
};

/**
 * Automatically sets default values in initialValues for fields that have exactly one option.
 * This is useful for pre-filling forms where certain fields have only one valid choice.
 */
export const setDefaultValuesForSingleOptions = (data: any): DataStructure => {
  const result: DataStructure = structuredClone(data);

  result.initialValues = Object.entries(result.initialValues.fields).reduce(
    (acc, [fieldName, fieldConfig]) => {
      // If the field has exactly one option, set it as the default value
      // This helps in pre-filling the form with the only available choice
      if (
        fieldConfig?.options?.length === 1 &&
        typeof acc[fieldName] === 'string' &&
        acc[fieldName].length === 0
      ) {
        acc[fieldName] = fieldConfig.options[0];
      }

      return acc;
    },
    result.initialValues,
  );

  return result;
};

export const setPayloadData = ({
  values,
  t,
}: {
  values: any;
  t: TFunction<'translation', undefined>;
}) => {
  const missingFields = [];
  const result = {};

  const handleContainsRequiredFields = (key, levels) => {
    if (levels.length > 1 && values.fields[key]?.required && !values[key]) {
      const [parent] = levels;

      const isEmpty = !values[key];

      const areSiblingsFilled = () => {
        return Object.keys(values.fields).some(
          k => k.startsWith(parent) && values[k],
        );
      };

      if ((values.fields[parent]?.required || areSiblingsFilled()) && isEmpty) {
        missingFields.push({
          type: useAppMessages.type.WARNING,
          msg: `${t('componentTestDataPane_missing')} "${t(
            `componentTestDataPane_${levels[levels.length - 1]}_label`,
          )}" ${t('componentTestDataPane_under')} ${t(
            `componentTestDataPane_${levels[levels.length - 2]}_label`,
          )}`,
        });
      }
    }
  };

  for (const key in values) {
    if (key !== 'fields') {
      const levels = key.split('--');
      levels.reduce((acc, part, index) => {
        if (index === levels.length - 1) {
          handleContainsRequiredFields(key, levels);
          if (part.includes('companion_sims')) {
            acc[part] = values[key]
              ?.split(',')
              .filter(v => v)
              .map(iccid => ({ iccid }));
          } else {
            acc[part] = values[key];
          }
        } else {
          acc[part] = acc[part] || {};
        }
        return acc[part];
      }, result);
    }
  }

  return { result, missingFields };
};
